import { Modality } from 'src/types/api/data/image';
import {
  JobListSchema,
  JobState,
  JobType,
  JobAssignRequest,
  JobReadSchema,
} from 'src/types/api/data/job';
import { dataApi } from 'src/utils/http';

import { shouldUseV3Api } from './helpers';

type JobAssignmentMutationParams = {
  projectId: string;
  userId: string;
  count: number;
  modality: Modality;
};

export const assignJobsByProject = async ({
  projectId,
  userId,
  count,
  modality,
}: JobAssignmentMutationParams): Promise<JobAssignRequest> => {
  const useV3 = await shouldUseV3Api(projectId, modality);
  if (useV3) {
    return assignV3JobsByProject({ projectId, userId, count, modality });
  }
  return assignV2JobsByProject({ projectId, userId, count, modality });
};

export const assignV3JobsByProject = async ({
  projectId,
  userId,
  count,
  modality,
}: JobAssignmentMutationParams): Promise<JobAssignRequest> => {
  // Fetch jobs assigned to the user to determine specific job IDs to unassign
  const jobsResponse = await getJobs({
    projectId,
    page: 1,
    pageSize: count,
    modality,
    annotatorId: userId,
    // Consider jobs that are not assigned to any user
    states: ['created'] as JobState[],
  });

  const jobIds = jobsResponse.jobs.map(j => j.id).slice(0, count);

  if (jobIds.length === 0) {
    return { success: [], failure: [] };
  }

  const results = await Promise.allSettled(
    jobIds.map(jobId =>
      dataApi.post(
        `/v3/projects/${projectId}/jobs/${jobId}/assign`,
        { assigneeId: userId },
        {
          params: { modality: modality.toLowerCase() },
        }
      )
    )
  );

  const success: string[] = [];
  const failure: string[] = [];
  results.forEach((res, idx) => {
    const id = jobIds[idx];
    if (!id) return;
    if (res.status === 'fulfilled') success.push(id);
    else failure.push(id);
  });

  return { success, failure };
};

export const assignV2JobsByProject = async ({
  projectId,
  userId,
  count,
  modality,
}: JobAssignmentMutationParams): Promise<JobAssignRequest> => {
  const payload = { count };
  const { data } = await dataApi.patch<JobAssignRequest>(
    `/projects/${projectId}/jobs/assign/`,
    payload,
    {
      params: {
        modality: modality.toLowerCase(),
        user: userId,
      },
    }
  );
  return data;
};

export const unassignV3JobsByProject = async ({
  projectId,
  userId,
  count,
  modality,
}: JobAssignmentMutationParams): Promise<JobAssignRequest> => {
  // Fetch jobs assigned to the user to determine specific job IDs to unassign
  const jobsResponse = await getJobs({
    projectId,
    page: 1,
    pageSize: count,
    modality,
    annotatorId: userId,
    // Consider jobs that are currently assigned to the user
    states: ['assigned', 'queued'] as JobState[],
  });

  const jobIds = jobsResponse.jobs.map(j => j.id).slice(0, count);

  if (jobIds.length === 0) {
    return { success: [], failure: [] };
  }

  const results = await Promise.allSettled(
    jobIds.map(jobId =>
      dataApi.post(
        `/v3/projects/${projectId}/jobs/${jobId}/unassign`,
        undefined,
        {
          params: { modality: modality.toLowerCase() },
        }
      )
    )
  );

  const success: string[] = [];
  const failure: string[] = [];
  results.forEach((res, idx) => {
    const id = jobIds[idx];
    if (!id) return;
    if (res.status === 'fulfilled') success.push(id);
    else failure.push(id);
  });

  return { success, failure };
};

export const unassignJobsByProject = async (
  args: JobAssignmentMutationParams
): Promise<JobAssignRequest> => {
  // v2 path is not available; keep v3 behavior
  return unassignV3JobsByProject(args);
};

export type getJobsProps = {
  projectId: string;
  page: number;
  pageSize: number;
  filter?: string;
  modality: Modality;
  annotatorId?: string;
  type?: JobType;
  states: JobState[];
};

// Get jobs of project
export const getJobs = async ({
  projectId,
  page,
  pageSize,
  filter,
  modality,
  annotatorId,
  type,
  states,
}: getJobsProps): Promise<JobListSchema> => {
  const useV3 = await shouldUseV3Api(projectId, modality);
  if (useV3) {
    return getV3Jobs({
      projectId,
      page,
      pageSize,
      filter,
      modality,
      annotatorId,
      type,
      states,
    });
  }
  return getV2Jobs({
    projectId,
    page,
    pageSize,
    filter,
    modality,
    annotatorId,
    type,
    states,
  });
};

export const getV3Jobs = async ({
  projectId,
  page,
  pageSize,
  filter,
  modality,
  annotatorId,
  type,
  states,
}: getJobsProps): Promise<JobListSchema> => {
  const { data } = await dataApi.get<JobListSchema>(
    `/v3/projects/${projectId}/jobs/`,
    {
      params: {
        project: projectId,
        page,
        page_size: pageSize,
        modality: modality.toLowerCase(),
        annotator: annotatorId,
        filter,
        type,
        states,
      },
    }
  );
  if (data.results) {
    data.jobs = data.results as unknown as JobReadSchema[];
  }
  return data;
};

export const getV2Jobs = async ({
  projectId,
  page,
  pageSize,
  filter,
  modality,
  annotatorId,
  type,
  states,
}: getJobsProps): Promise<JobListSchema> => {
  const { data } = await dataApi.get<JobListSchema>(
    `/v2/projects/${projectId}/jobs`,
    {
      params: {
        page,
        page_size: pageSize,
        modality: modality.toLowerCase(),
        annotator: annotatorId,
        filter,
        type,
        states,
      },
    }
  );
  return data;
};
